import { memo, ReactNode, useCallback, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import Button from "../../Button"
import { useClickOutside } from "../../../hooks/useClickOutside"
import DropDownSelection from "../../DropdownSelection"
import { KEY } from "../../../data/variables"

import './index.scss'

type ButtonSelect = {
  label: string | ReactNode
  options: { [key: string | number]: string | ReactNode }
  onChange: (val: string | number) => void
  size?: 'l' | 'm' | 's'
  closeOnSelection?: boolean
  dropdownAlign?: 'left' | 'right'
  dropdownWidth?: number
}

const ButtonSelect = memo(({
  label, options, onChange, size = 's', closeOnSelection = true, dropdownAlign = 'right',
  dropdownWidth
}: ButtonSelect) => {
  const dropdownRef = useRef()
  const boundsRef = useRef()

  const [isOpen, setIsOpen] = useState<boolean>(false)

  useClickOutside(dropdownRef, () => setIsOpen(false))

  const onKeyDown = useCallback((e: KeyboardEvent, id: string | number) => {
    if (e.key === KEY.ENTER) {
      onChange(id)
      if (closeOnSelection) setIsOpen(false)
    }
  }, [onChange, closeOnSelection])

  const selectionOptions = useMemo(() => (
    Object.keys(options).map((id) => ({
      label: options[id],
      onClick: () => {
        onChange(id)
        if (closeOnSelection) setIsOpen(false)
      },
      onKeyDown: (e: KeyboardEvent) => onKeyDown(e, id)
    }))
  ), [options])
  
  return (
    <div className="ButtonSelect">
      <Button
        ref={boundsRef}
        label={label}
        type={isOpen ? 'secondary' : 'tertiary'}
        size={size}
        onClick={() => setIsOpen(!isOpen)}
      />
      {isOpen && createPortal(
        <div ref={dropdownRef} className="ButtonSelect__dropdown">
          <DropDownSelection
            boundsRef={boundsRef}
            options={selectionOptions}
            align={dropdownAlign}
            width={dropdownWidth}
          />
        </div>,
        document.body
      )}
    </div>
  )
})

export default ButtonSelect
